import type { Platform } from '../lib/platform';

/**
 * Wire types for Discovery's email outreach (`/api/email/*`). Mirrors the
 * discovery backend's routes/email.ts — an HTTP contract, duplicated on
 * purpose like the rest of `api/types.ts`.
 */

export const EMAIL_KINDS = ['personal', 'management', 'business'] as const;
export type EmailKind = (typeof EMAIL_KINDS)[number];

export const FACE_SEGMENTS = ['face', 'faceless', 'unknown'] as const;
export type FaceSegment = (typeof FACE_SEGMENTS)[number];

export const EXCLUSION_REASONS = [
  'alreadyEmailed',
  'inConversation',
  'inQueue',
  'joined',
  'doNotEmail',
  'suppressed',
  'unsendable',
] as const;
export type ExclusionReason = (typeof EXCLUSION_REASONS)[number];

export type AudienceOrder = 'followers' | 'newest' | 'random';

/** The audience filter as sent to the server. Lists are sent comma-joined on GETs. */
export interface AudienceFilter {
  search?: string;
  folders?: string[];
  purposes?: string[];
  minFollowers?: number;
  maxFollowers?: number;
  minAvgViews?: number;
  regions?: string[];
  kinds?: EmailKind[];
  faces?: FaceSegment[];
  platforms?: Platform[];
  accountTypes?: string[];
  /** Only creators whose display name gives a real first name for the greeting. */
  namedOnly?: boolean;
  /** Creators unticked by hand in the preview. */
  excludeCreatorIds?: number[];
  /** Off unless true. */
  includeEmailed?: boolean;
  /** On unless false: creators an outreacher has messaged or logged. */
  includeInConversation?: boolean;
  /** On unless false: creators in the DM queue, not messaged yet. */
  includeInQueue?: boolean;
}

export interface AudienceCubeCell {
  kind: EmailKind;
  face: FaceSegment;
  platform: Platform;
  accountType: string | null;
  count: number;
}

/** One folder's eligible creators, counted as if no folder were picked. */
export interface AudienceFolderCount {
  publicId: string;
  name: string;
  purpose: string;
  archived: boolean;
  eligible: number;
  /** Of those, how many the email type / on camera / platform / account type filters select. */
  matching: number;
  kinds: Record<EmailKind, number>;
  faces: Record<FaceSegment, number>;
}

export interface AudienceSummary {
  withEmail: number;
  excluded: Record<ExclusionReason, number>;
  sharedAddress: number;
  eligible: number;
  matching: number;
  cube: AudienceCubeCell[];
  /** Absent on servers older than the folder picker. */
  folders?: AudienceFolderCount[];
  /** Selected addresses by creator country, ignoring the country filter. Absent on older servers. */
  regions?: { region: string; count: number }[];
}

export interface AudienceRow {
  creatorId: number;
  platform: Platform;
  username: string;
  nickname: string | null;
  email: string;
  kind: EmailKind;
  face: FaceSegment;
  accountType: string | null;
  followers: number | null;
  avgViews: number | null;
  region: string | null;
  profileUrl: string | null;
  outreachState: string;
  folders: string[];
}

export interface TrackingStatus {
  ready: boolean;
  url: string | null;
  registered: boolean;
  disabled: boolean;
  problem: string | null;
}

export interface EmailStatus {
  instantly: { connected: boolean };
  tracking: TrackingStatus | null;
  /** Origin of tracked links, or null when links go straight to their destination. Absent on older servers. */
  linkTracking?: { base: string | null };
  maxBatch: number;
  linkPlaceholders: string[];
}

export interface InstantlyCampaignOption {
  id: string;
  name: string;
  status: number;
  statusLabel: string;
  createdAt: string | null;
  /** Set when this Instantly campaign is already linked here. */
  linkedCampaignId: string | null;
}

export interface Funnel {
  total: number;
  queued: number;
  failed: number;
  skipped: number;
  added: number;
  sent: number;
  opened: number;
  clicked: number;
  replied: number;
  interested: number;
  bounced: number;
  unsubscribed: number;
  /** Signed up on the platform after being emailed (an account with their handle). */
  joined: number;
  /** Posted after being emailed. Absent on servers older than the joined/posted split. */
  posted?: number;
}

export type EmailProvider = 'instantly' | 'csv';

export interface EmailCampaign {
  publicId: string;
  name: string;
  provider: EmailProvider;
  providerCampaignId: string | null;
  linkTemplate: string | null;
  /** Links go through our redirect to count clicks per creator. Off by default. */
  trackClicks?: boolean;
  notes: string | null;
  lastFilter: AudienceFilter | null;
  providerStats: Record<string, unknown> | null;
  lastSyncedAt: string | null;
  lastSyncError: string | null;
  /** Emails in the Instantly sequence, once synced. */
  stepCount?: number | null;
  createdByName: string | null;
  archivedAt: string | null;
  createdAt: string;
}

/**
 * Where a campaign's creators are in the sequence. `steps[i]` = had email i+1
 * and nothing later yet. `finished` = all emails sent, or stopped by a reply,
 * bounce or unsubscribe. Absent on servers older than the sequence view.
 */
export interface SequenceProgress {
  notSent: number;
  steps: number[];
  finished: number;
}

export interface EmailCampaignWithFunnel extends EmailCampaign {
  funnel: Funnel;
  progress?: SequenceProgress;
}

export const BREAKDOWN_DIMENSIONS = ['kind', 'face', 'platform', 'accountType', 'size'] as const;
export type BreakdownDimension = (typeof BREAKDOWN_DIMENSIONS)[number];

export interface BreakdownRow extends Funnel {
  key: string;
}

export type Breakdowns = Record<BreakdownDimension, BreakdownRow[]>;

export interface CampaignDetail {
  campaign: EmailCampaign;
  funnel: Funnel;
  progress?: SequenceProgress;
  breakdowns: Breakdowns;
}

export interface BatchResult {
  campaign: EmailCampaign;
  selected: number;
  recorded: number;
  added: number;
  skipped: number;
  failed: number;
  error: string | null;
}

export interface BatchInput {
  filter: AudienceFilter;
  limit?: number;
  order?: AudienceOrder;
  verify?: boolean;
}

export type NewCampaignInput = BatchInput &
  (
    | { provider: 'instantly'; providerCampaignId: string; name?: string; linkTemplate?: string; trackClicks?: boolean }
    | { provider: 'csv'; name: string; linkTemplate?: string; trackClicks?: boolean }
  );

export type EmailSendStatus =
  | 'queued' | 'pushed' | 'exported' | 'sent' | 'opened' | 'clicked' | 'replied'
  | 'bounced' | 'unsubscribed' | 'skipped' | 'failed';

export interface Recipient {
  id: number;
  creatorId: number;
  email: string;
  emailKind: EmailKind;
  platform: Platform;
  username: string;
  segment: { face: FaceSegment; accountType: string | null; followers: number | null; folders: string[] } | null;
  /** The link as sent: our redirect when click tracking is on. */
  inviteLink: string | null;
  /** Where it leads. */
  linkDestination: string | null;
  status: EmailSendStatus;
  detail: string | null;
  interest: string | null;
  openCount: number;
  clickCount: number;
  replyCount: number;
  lastStep: number | null;
  pushedAt: string | null;
  sentAt: string | null;
  openedAt: string | null;
  clickedAt: string | null;
  repliedAt: string | null;
  bouncedAt: string | null;
  unsubscribedAt: string | null;
  joinedAt: string | null;
  postedAt?: string | null;
  /** When the latest email (`lastStep`, 1-based) went out, and the mailbox that sent it. */
  lastStepAt?: string | null;
  lastSender?: string | null;
  /** The sequence is over for them: all sent, or stopped by a reply, bounce or unsubscribe. */
  finishedAt?: string | null;
  lastEventAt: string | null;
  outreachState: string | null;
}

export interface RecipientQuery {
  stage?: string;
  kind?: string;
  face?: string;
  platform?: string;
  search?: string;
  limit?: number;
  offset?: number;
}
